import Alert from 'react-s-alert';
import { action, computed } from 'mobx';
import i18next from 'i18next';

import { AppManager, Variables, Input } from 'core/index';

import { raiseUserError } from 'apps/utils';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';

import { IssueModel } from 'apps/issueTracker/models';
import { IssueApp } from 'apps/issueTracker/apps/issueApp';

import { IssueQuery, IssueMutation } from 'apps/issueTracker/gql';

export class IssuePageAppManager extends AppManager {
  static registryName = 'IssuePageAppManager';
  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('issuePage', IssueApp);
    this.variables = new Variables({
      space: () => this.params.get('profile') || this.params.get('initiative'),
      slug: () => this.params.get('slug'),
      issueSlug: () => this.params.get('issueSlug'),
    });
    this.variables.onChange(['space', 'slug', 'issueSlug'], this.NewVariables);
    this.issueInput = new Input({
      id: () => this.issuePage.id,
      status: () => this.issuePage?.status,
      title: () => this.issuePage?.title, // NOTE: Not used yet. Needs editing interface...
      description: () => this.issuePage?.description, // NOTE: Not used yet. Needs editing interface...
      labels: () =>
        this.issuePage?.labels.map(label => ({
          labelId: label.id,
          deleted: label.deleted,
        })),
      assignees: () =>
        this.issuePage?.assignees.map(assignee => ({
          profileId: assignee.id,
          deleted: assignee.deleted,
        })),
      tags: () => this.issuePage?.tags.map(({ name }) => ({ name })),
    });
  }

  @computed get loading() {
    return this.queries.get('issuePage')?.loading;
  }

  onSaveErr = error => {
    this.issuePage?.form.SetPosting(false);
    clientStore.SetPosting(false);
    raiseUserError(error, 'save', 'Issue');
    this.issuePage?.ResetLocals();
  };

  onSaveSuccess = () => {
    this.issuePage?.form.SetPosting(false);
    this.issuePage?.form.clearDraft();
    clientStore.SetPosting(false);
    this.issuePage?.ResetLocals();
    this.issuePage?.SetEditing(false);
    Alert.success('Issue updated successfully!');
    clientStore.context.delete('issueApp');
  };

  Save = () => {
    const { issuePage } = this;
    if (!issuePage) return;
    if (!issuePage.form.isValid) {
      issuePage.form.alert();
      return;
    }
    issuePage.form.SetPosting(true);
    clientStore.SetPosting(true);
    this.UpdateIssue({
      onSysErr: this.onSaveErr,
      onUserErr: this.onSaveErr,
      onSuccess: this.onSaveSuccess,
    });
  };

  EditIssue = () => {
    const { issuePage } = this;
    if (!issuePage) return;
    issuePage.createForm();
    issuePage.SetEditing(true);
    clientStore.context.set('issueApp', issuePage);
  };

  Cancel = () => {
    const { issuePage } = this;
    if (!issuePage) return;
    issuePage.ResetLocals();
    issuePage.SetEditing(false);
    issuePage.editFormStore = null;
    clientStore.context.delete('issueApp');
  };

  onAddAssigneesError = error => {
    this.issuePage?.addAssigneesForm.SetPosting(false);
    clientStore.SetPosting(false);
    raiseUserError(error, 'add', 'Assignees');
  };

  onAddAssigneesSuccess = result => {
    this.issuePage?.ResetLocals();
    modalStore.ClearComponent();
    this.issuePage?.addAssigneesForm.SetPosting(false);
    this.issuePage?.addAssigneesForm.clearDraft();
    clientStore.SetPosting(false);
    const { number } = result;
    Alert.success(
      i18next.t('Assignee(s) added to Issue #{{number}} successfully!', {
        number,
      }),
    );
  };

  AddAssignees = () => {
    const { issuePage } = this;
    if (!issuePage) return;
    if (!issuePage.addAssigneesForm.isValid) {
      issuePage.addAssigneesForm.alert();
    }
    issuePage.addAssigneesForm.SetPosting(true);
    clientStore.SetPosting(true);
    this.UpdateIssue({
      onSysErr: this.onAddAssigneesError,
      onUserErr: this.onAddAssigneesError,
      onSuccess: this.onAddAssigneesSuccess,
    });
  };

  onAddLabelsError = error => {
    this.issuePage?.addLabelsForm.SetPosting(false);
    clientStore.SetPosting(false);
    raiseUserError(error, 'add', 'Labels');
  };

  onAddLabelsSuccess = result => {
    this.issuePage?.ResetLocals();
    this.issuePage?.addLabelsForm.SetPosting(false);
    this.issuePage?.addLabelsForm.clearDraft();
    modalStore.ClearComponent();
    clientStore.SetPosting(false);
    const { number } = result;
    Alert.success(
      i18next.t('Labels updated successfully for Issue #{{number}}! 🏷', {
        number,
      }),
    );
  };

  AddLabels = () => {
    const { issuePage } = this;
    if (!issuePage) return;
    if (!issuePage.addLabelsForm.isValid) {
      issuePage.addLabelsForm.alert();
    }
    issuePage.addLabelsForm.SetPosting(true);
    clientStore.SetPosting(true);
    this.UpdateIssue({
      onSysErr: this.onAddLabelsError,
      onUserErr: this.onAddLabelsError,
      onSuccess: this.onAddLabelsSuccess,
    });
  };

  onCloseIssueError = error => {
    clientStore.SetPosting(false);
    this.issuePage?.ResetLocals();
    raiseUserError(error, 'close', 'Issue');
  };

  onCloseIssueSuccess = () => {
    this.issuePage?.ResetLocals();
    clientStore.SetPosting(false);
    const { number } = this.issuePage;
    Alert.success(
      i18next.t('Issue #{{number}} closed successfully! Nice work!', {
        number,
      }),
    );
  };

  CloseIssue = () => {
    const { issuePage } = this;
    if (!issuePage) return;
    issuePage.CloseIssue();
    clientStore.SetPosting(true);
    this.UpdateIssue({
      onSysErr: this.onCloseIssueError,
      onUserErr: this.onCloseIssueError,
      onSuccess: this.onCloseIssueSuccess,
    });
  };

  onReOpenIssueError = error => {
    this.issuePage?.ResetLocals();
    raiseUserError(error, 're-open', 'Issue');
    clientStore.SetPosting(false);
  };

  onReOpenIssueSuccess = () => {
    this.issuePage?.ResetLocals();
    clientStore.SetPosting(false);
    const { number } = this;
    Alert.success(
      i18next.t('Issue #{{number}} re-open successfully! Good luck!', {
        number,
      }),
    );
  };

  ReOpenIssue = () => {
    const { issuePage } = this;
    if (!issuePage) return;
    issuePage.ReOpenIssue();
    clientStore.SetPosting(true);
    this.UpdateIssue({
      onSysErr: this.onReOpenIssueError,
      onUserErr: this.onReOpenIssueError,
      onSuccess: this.onReOpenIssueSuccess,
    });
  };

  onDeleteError = error => {
    clientStore.SetPosting(false);
    this.issuePage?.ResetLocals();
    raiseUserError(error, 'delete', 'Issue');
  };

  onDeleteSuccess = () => {
    clientStore.SetPosting(false);
    this.issuePage?.ResetLocals();
    modalStore.ClearComponent();
    const { route } = clientStore.routerStore;
    if (route.path.includes('/@')) {
      clientStore.routerStore.navigate('profile.project', {
        profile: route.params.profile,
        slug: route.params.slug,
      });
    } else if (route.path.includes('/+')) {
      clientStore.routerStore.navigate('initiative.project', {
        initiative: route.params.initiative,
        slug: route.params.slug,
      });
    }
    Alert.success('Issue deleted successfully');
  };

  Delete = () => {
    const { issuePage } = this;
    if (!issuePage) return;
    issuePage.Delete();
    clientStore.SetPosting(true);
    this.UpdateIssue({
      onSysErr: this.onDeleteError,
      onUserErr: this.onDeleteError,
      onSuccess: this.onDeleteSuccess,
    });
  };

  @action NewVariables = () => {
    const q = new IssueQuery(this, {
      queryKey: 'issuePage',
      onSysErr: this.onError,
    });
    q.process(issuePage => IssueModel.Load(issuePage), this.IssuePageUpdate);
    q.query();
  };

  @action IssuePageUpdate = issueModel => {
    this.issuePage = issueModel;
  };

  @action onError = () => {
    // ...
  };

  @action UpdateIssue = options => {
    this.mutations.push(IssueMutation.New(this.issueInput, options));
  };
}
